import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@gettactic/helpers";

const switchVariants = cva(
  "peer inline-flex shrink-0 cursor-pointer items-center border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-neutral-300 data-[state=unchecked]:bg-neutral-500",
  {
    variants: {
      variant: {
        rounded: "rounded-full",
        squared: "rounded-[3px]"
      },
      size: {
        xs: "h-3.5 w-4",
        sm: "h-5 w-9",
        md: "h-6 w-11",
        xl: "h-7 w-12",
        "2xl": "h-8 w-14"
      },
      color: {
        default: "data-[state=checked]:bg-primary",
        destructive: "data-[state=checked]:bg-red-600"
      }
    },
    defaultVariants: {
      variant: "rounded",
      size: "md",
      color: "default"
    }
  }
);

const thumbVariants = cva(
  "pointer-events-none block bg-background shadow-lg ring-0 transition-transform",
  {
    variants: {
      variant: {
        rounded: "rounded-full",
        squared: "rounded-[1.5px]"
      },
      size: {
        xs: "h-[11px] w-1.5 data-[state=checked]:translate-x-1.5 data-[state=unchecked]:translate-x-0",
        sm: "h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
        md: "h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
        xl: "h-6 w-6 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0",
        "2xl":
          "h-7 w-7 data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-0"
      }
    },
    defaultVariants: {
      variant: "rounded",
      size: "md"
    }
  }
);

// Native implementation replaces @radix-ui/react-switch to avoid an infinite
// re-render loop caused by Radix's useComposedRefs creating an unstable ref
// callback on every render (incompatible with React 19's ref cleanup semantics).
interface SwitchProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "color">,
    VariantProps<typeof switchVariants> {
  label?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  required?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      className,
      variant,
      size,
      color,
      label,
      checked: checkedProp,
      defaultChecked,
      onCheckedChange,
      disabled,
      onClick,
      name,
      value = "on",
      required,
      form,
      ...props
    },
    ref
  ) => {
    const isControlled = checkedProp !== undefined;
    const [internalChecked, setInternalChecked] = React.useState(
      defaultChecked ?? false
    );
    const checked = isControlled ? checkedProp : internalChecked;
    const state = checked ? "checked" : "unchecked";

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      const next = !checked;
      if (!isControlled) setInternalChecked(next);
      onCheckedChange?.(next);
      onClick?.(event);
    };

    return (
      // biome-ignore lint/a11y/noLabelWithoutControl: <explanation>
      <label className="flex cursor-pointer items-center gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-required={required}
          data-state={state}
          data-disabled={disabled ? "" : undefined}
          disabled={disabled}
          className={cn(
            switchVariants({ variant, size, color }),
            className
          )}
          ref={ref}
          onClick={handleClick}
          {...props}
        >
          <span
            data-state={state}
            data-disabled={disabled ? "" : undefined}
            className={cn(thumbVariants({ variant, size }))}
          />
        </button>
        {name && (
          <input
            type="checkbox"
            aria-hidden
            tabIndex={-1}
            checked={checked}
            name={name}
            value={value}
            required={required}
            disabled={disabled}
            form={form}
            readOnly
            style={{
              position: "absolute",
              pointerEvents: "none",
              opacity: 0,
              margin: 0,
              transform: "translateX(-100%)"
            }}
          />
        )}
        {label && (
          <span className="text-neutral-1000 text-sm">{label}</span>
        )}
      </label>
    );
  }
);

Switch.displayName = "Switch";

export { Switch };
export type { SwitchProps };
